import {BOT_MAX_STEPS_PER_PHASE} from '../botConfig';
import {BOT_PHASE_COMPLETION_MODES, BOT_STEP_RESULTS} from '../botStepResults';
import {BOT_CONSTRUCTION_TURN_PACINGS} from './constructionTurnPacing';

const checkIfPlannedPacing = (pacing) => pacing === BOT_CONSTRUCTION_TURN_PACINGS.PLANNED
  || pacing === BOT_CONSTRUCTION_TURN_PACINGS.PLANNED_AUTO_COMPLETE;

/**
 * Тики исполнителя делегированной работы бота с учетом темпа хода
 * строительства (pacing, см. constructionTurnPacing):
 *  - PACED (и любая работа вне хода строительства) — как раньше: первый шаг
 *    после обычной паузы, дальше шаг за шагом с паузами;
 *  - PLANNED — первый тик сразу, без паузы, ставит все строения разом (шаги
 *    подряд, с отложенным завершением), ход завершается следующим тиком,
 *    через обычную паузу;
 *  - PLANNED_AUTO_COMPLETE — первый тик сразу ставит все строения и тут же
 *    завершает ход.
 * В обоих «спланированных» темпах, если бот решил купить политику, решение
 * «планировал» отменяется: план живого игрока ставит только строения, а
 * политику он покупает сам, кликом, — мгновенная покупка в начале хода (и тем
 * более мгновенно законченный с ней ход) выглядела бы подозрительно. Ход
 * дальше идет обычным темпом — покупка и все остальное с паузами.
 *
 * runStep({phaseCompletion, holdPolicyPurchase}) — шаг бота (executeBotStep),
 * возвращает его итог; checkIfWorkActual() — работа все еще эта (ключ хода не
 * сменился); getActionDelayMs() — обычная пауза бота.
 *
 * Возвращает {getFirstDelayMs, step}: step() делает тик и возвращает паузу до
 * следующего.
 */
export const createBotWorkStepper = ({pacing: initialPacing, runStep, checkIfWorkActual, getActionDelayMs}) => {
  let pacing = initialPacing;
  let isCompletionDue = false;

  // Шаги подряд, пока бот что-то делает. Страховка от зацикливания — тот же
  // кап, что у движка на фазу: дальше он сам завершит фазу
  const buildAllPlanned = () => {
    const stepOptions = {phaseCompletion: BOT_PHASE_COMPLETION_MODES.DEFER, holdPolicyPurchase: true};
    let result = runStep(stepOptions);
    for (let steps = 1; result === BOT_STEP_RESULTS.ACTED && steps <= BOT_MAX_STEPS_PER_PHASE; steps++) {
      if (!checkIfWorkActual()) {
        return null;
      }
      result = runStep(stepOptions);
    }
    return result;
  };

  const step = () => {
    if (!checkIfPlannedPacing(pacing)) {
      runStep({phaseCompletion: BOT_PHASE_COMPLETION_MODES.AUTO});
      return getActionDelayMs();
    }

    if (isCompletionDue) {
      isCompletionDue = false;
      runStep({phaseCompletion: BOT_PHASE_COMPLETION_MODES.FORCE});
      return getActionDelayMs();
    }

    const result = buildAllPlanned();
    if (result === BOT_STEP_RESULTS.POLICY_PURCHASE_WANTED) {
      // Решение «планировал» отменено: дальше — как обычно
      pacing = BOT_CONSTRUCTION_TURN_PACINGS.PACED;
      return getActionDelayMs();
    }
    if (result === BOT_STEP_RESULTS.READY_TO_COMPLETE && checkIfWorkActual()) {
      if (pacing === BOT_CONSTRUCTION_TURN_PACINGS.PLANNED_AUTO_COMPLETE) {
        runStep({phaseCompletion: BOT_PHASE_COMPLETION_MODES.FORCE});
      } else {
        isCompletionDue = true;
      }
    }
    return getActionDelayMs();
  };

  return {
    // «Спланированный» ход начинается сразу: план живого игрока тоже ставится
    // без паузы. Обычный — после такой же паузы, как между шагами
    getFirstDelayMs: () => (checkIfPlannedPacing(pacing) ? 0 : getActionDelayMs()),
    step,
    // Текущий темп: «спланированный» может смениться обычным (покупка политики)
    getPacing: () => pacing,
  };
};
